import React, { useState, useEffect, useRef } from 'react';
import './VideoPlayer.css';
import Hls from 'hls.js';

const VideoPlayer = ({ video }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(video.title || (video.fileName ? video.fileName.substring(0, video.fileName.lastIndexOf('.')) : 'Untitled Video'));
  const [description, setDescription] = useState(video.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [videoError, setVideoError] = useState(null);
  
  // Update title and description when a different video is selected
  useEffect(() => {
    setTitle(video.title || (video.fileName ? video.fileName.substring(0, video.fileName.lastIndexOf('.')) : 'Untitled Video'));
    setDescription(video.description || '');
    setIsEditing(false);
    setError(null);
  }, [video]);
  
  // Reference to the video element
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  
  // Set up HLS.js when the component mounts or video changes
  useEffect(() => {
    console.log('VideoPlayer: video changed', video);
    
    // Clean up previous HLS instance if it exists
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    
    // Reset error state
    setVideoError(null);
    
    // Function to initialize HLS
    const initializeHls = () => {
      if (videoRef.current && video.hlsUrl) {
        console.log('Initializing HLS player with URL:', video.hlsUrl);
        
        // Check if HLS is supported in the browser
        if (Hls.isSupported()) {
          console.log('HLS is supported, initializing player');
          
          // Create a new HLS instance
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            debug: true
          });
          
          // Store the HLS instance in the ref
          hlsRef.current = hls;
          
          // Bind HLS to the video element
          hls.attachMedia(videoRef.current);
          
          // Load the source
          hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            console.log('HLS media attached');
            hls.loadSource(video.hlsUrl);
          });
          
          // Handle errors
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error('HLS error:', data);
            if (data.fatal) {
              switch(data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  console.error('Fatal network error', data);
                  setVideoError(`Network error: ${data.details}`);
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  console.error('Fatal media error', data);
                  setVideoError(`Media error: ${data.details}`);
                  break;
                default:
                  console.error('Fatal error', data);
                  setVideoError(`Error: ${data.details}`);
                  break;
              }
            }
          });
          
          // Handle manifest parsed event
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log('HLS manifest parsed, starting playback');
            videoRef.current.play().catch(e => {
              console.error('Error playing video:', e);
              setVideoError('Error starting playback. Try clicking the play button.');
            });
          });
        } 
        // For browsers that natively support HLS (like Safari)
        else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          console.log('Native HLS support detected');
          videoRef.current.src = video.hlsUrl;
          videoRef.current.addEventListener('loadedmetadata', () => {
            videoRef.current.play().catch(e => {
              console.error('Error playing video:', e);
              setVideoError('Error starting playback. Try clicking the play button.');
            });
          });
        } else {
          console.error('HLS is not supported in this browser');
          setVideoError('HLS video playback is not supported in this browser.');
        }
      }
    };
    
    // Initialize HLS if the video is ready
    if (video.status?.toLowerCase() === 'complete' && video.hlsUrl) {
      console.log('Video is ready, URL:', video.hlsUrl);
      initializeHls();
    } else {
      console.log('Video is not ready for playback:', video.status);
    }
    
    // Cleanup function
    return () => {
      if (hlsRef.current) {
        console.log('Destroying HLS instance');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [video]);

  // Function to handle metadata updates
  const handleUpdateMetadata = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Title cannot be empty');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Get the current auth session to include the token in the request
      const { idToken } = (await import('aws-amplify/auth')).fetchAuthSession().tokens ?? {};
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }
      
      // Use fetch directly with the API URL
      const apiUrl = `https://f8r84a0hqe.execute-api.us-east-1.amazonaws.com/prod/videos/${video.videoId}/info`;
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `${idToken.toString()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      // Exit edit mode
      setIsEditing(false);
      setSaving(false);
      
      // Update the video object in the parent component
      // This is handled through the API refresh in the Videos component
      
    } catch (err) {
      console.error('Error updating video metadata:', err);
      setError('Failed to update video information. Please try again.');
      setSaving(false);
    }
  };

  // Function to format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Use the correct URL field from the video object
  const videoUrl = video.hlsUrl || video.url;
  const thumbnailUrl = video.thumbnailUrl;
  const isVideoReady = video.status?.toLowerCase() === 'complete';

  return (
    <div className="video-player">
      <div className="video-wrapper">
        {isVideoReady && videoUrl ? (
          <>
            <video 
              ref={videoRef}
              controls 
              poster={thumbnailUrl}
              className="video-element"
            >
              Your browser does not support the video tag.
            </video>
            {videoError && (
              <div className="video-error">
                <p>{videoError}</p>
                <p>URL: {videoUrl}</p>
              </div>
            )}
          </>
        ) : (
          <div className="video-processing">
            <p>This video is still being processed. Please check back later.</p>
            {thumbnailUrl && (
              <img src={thumbnailUrl} alt="Video thumbnail" className="processing-thumbnail" />
            )}
          </div>
        )}
      </div>
      
      <div className="video-info">
        {isEditing ? (
          <form onSubmit={handleUpdateMetadata} className="edit-form">
            {error && <p className="error-message">{error}</p>}
            
            <div className="form-group">
              <label htmlFor="edit-title">Title:</label>
              <input
                type="text"
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={saving}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="edit-description">Description:</label>
              <textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
                rows="3"
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="save-button"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => {
                  setIsEditing(false);
                  setTitle(video.title || (video.fileName ? video.fileName.substring(0, video.fileName.lastIndexOf('.')) : 'Untitled Video'));
                  setDescription(video.description || '');
                  setError(null);
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="video-header">
              <h2 className="video-title">{title}</h2>
              <button 
                className="edit-button"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            </div>
            
            <p className="video-date">Uploaded on {formatDate(video.createdAt || video.uploadDate)}</p>
            
            {description && (
              <div className="video-description">
                <h3>Description:</h3>
                <p>{description}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
